import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
import {parsePageNumber} from '@github-ui/agent-sessions/utils/url-helpers'
import {useCurrentUserOptional} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {getCreatedByMeSearchParams} from '@github-ui/agent-sessions/components/agents-view/queries'
import {getAgentsPath} from '@github-ui/agent-sessions/utils/constants'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {AgentIcon, AlertIcon} from '@primer/octicons-react'
import {Button, Link, Pagination, Stack} from '@primer/react'
import {useCallback, useState} from 'react'
import type {MouseEvent, ReactNode} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {AgentQueryQualifier} from '../types'
import {AgentsListItem} from './AgentsListItem'
import {clsx} from 'clsx'
import {
  DashboardListItem,
  DashboardListItemActions,
  DashboardListItemTitle,
  DashboardListView,
  DashboardListViewHeader,
  DashboardListViewHeaderMetadata,
  DashboardListViewHeaderTitle,
  DashboardListViewItems,
  DashboardListViewItemsSkeletonLoader,
} from './DashboardListView'
import styles from './DashboardListView.module.css'
import {ListActionMenu} from './ListActionMenu'
import {RESULT_COUNTS} from '../constants'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'

function AgentsListPagination({hasNextPage}: {hasNextPage: boolean}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = parsePageNumber(searchParams.get('page'))
  const hasPreviousPage = currentPage > 1
  const pageCount = hasNextPage ? currentPage + 1 : currentPage

  const onPageChange = (e: MouseEvent, page: number): void => {
    e.preventDefault()
    searchParams.set('page', page.toString())
    setSearchParams(searchParams)
  }

  if (!hasNextPage && !hasPreviousPage) return null
  return <Pagination showPages={false} pageCount={pageCount} currentPage={currentPage} onPageChange={onPageChange} />
}

type ValidResultCount = (typeof RESULT_COUNTS)[number]

interface BaseAgentsListProps {
  loadingState?: React.ReactElement
  initialResultCount?: ValidResultCount
  initialQueryQualifiers?: AgentQueryQualifier[]
  persistResultCount?: (count: number) => void
  persistQueryQualifiers?: (qualifiers: AgentQueryQualifier[]) => void
  title?: string
  showViewAll?: boolean
  emptyPlaceholder?: React.ReactNode
  enableEmptyListAction?: boolean
  analyticsCategory?: string
  activeTab?: 'active' | 'completed'
  setActiveTab?: (tab: 'active' | 'completed') => void
  includeCounts?: boolean
  /** Optional repository NWO filter (e.g., "owner/repo", "none" for repo-less tasks, or undefined for all repos) */
  repoFilter?: string
  /** Additional filter controls rendered in the header metadata area (right side) when showPillMenu is active */
  additionalFilters?: ReactNode
}

interface AgentsListWithFilterMenu extends BaseAgentsListProps {
  showFilterMenu: true
  showPillMenu?: never
  showPagination?: never
}

interface AgentsListWithPillMenu extends BaseAgentsListProps {
  showFilterMenu?: never
  showPillMenu: true
  showPagination: true
}

interface AgentsListWithoutMenus extends BaseAgentsListProps {
  showFilterMenu?: false
  showPillMenu?: false
  showPagination?: false
}

type AgentsListProps = AgentsListWithFilterMenu | AgentsListWithPillMenu | AgentsListWithoutMenus

export type {AgentsListProps}

const DEFAULT_RESULT_COUNT = 3
const DEFAULT_AGENT_QUERY_QUALIFIERS = [AgentQueryQualifier.Open]
const DEFAULT_LOADING_STATE = <DashboardListViewItemsSkeletonLoader />

const getViewAllSessionsHref = () => `${getAgentsPath()}?${getCreatedByMeSearchParams().toString()}`

export function AgentsList(props: AgentsListProps) {
  const {
    loadingState = DEFAULT_LOADING_STATE,
    initialResultCount = DEFAULT_RESULT_COUNT,
    initialQueryQualifiers = DEFAULT_AGENT_QUERY_QUALIFIERS,
    persistResultCount,
    persistQueryQualifiers,
    title = 'Agent sessions',
    showViewAll = false,
    showFilterMenu = false,
    showPillMenu = false,
    showPagination = false,
    emptyPlaceholder = 'No sessions found. Create one by sending a prompt above.',
    enableEmptyListAction = false,
    analyticsCategory = 'agents_list',
    activeTab,
    setActiveTab,
    includeCounts = false,
    repoFilter,
    additionalFilters,
  } = props

  const [searchParams, setSearchParams] = useSearchParams()
  const [resultCount, setResultCount] = useState<number>(initialResultCount)
  const [queryQualifiers, setQueryQualifiers] = useState<AgentQueryQualifier[]>(initialQueryQualifiers)

  const currentPage = parsePageNumber(searchParams.get('page'))

  const taskFilter = (() => {
    if (activeTab) return activeTab // open or closed

    // Derive from queryQualifiers when active tab not defined
    if (queryQualifiers.length === 0 || queryQualifiers.length === 2) {
      return 'all'
    }

    return queryQualifiers.includes(AgentQueryQualifier.Closed) ? 'completed' : 'active'
  })()

  // Fetch tasks for the current user.
  // The repo-scoped tasks endpoint returns tasks for all users in the repo by default,
  // so we forward the current user's ID as `creatorIds`. `useTasks` only applies it when
  // a repo NWO filter is active, so the global endpoint stays implicitly user-scoped.
  const currentUserContext = useCurrentUserOptional()
  const {tasks, isFetching, isLoading, isError, hasNextPage, counts} = useTasks({
    sessionsPollingInterval: 10000,
    taskFilter,
    includeCounts,
    currentPage,
    pageSize: resultCount,
    repoFilter,
    creatorIds: currentUserContext?.currentUser?.id ? [currentUserContext.currentUser.id] : undefined,
  })

  // Track page/tab/qualifiers to detect navigation vs optimistic updates
  const [prevPage, setPrevPage] = useState(currentPage)
  const [prevTab, setPrevTab] = useState(activeTab)
  const [prevQualifiers, setPrevQualifiers] = useState(queryQualifiers)

  // Detect navigation by comparing current and previous values
  const isNavigating = prevPage !== currentPage || prevTab !== activeTab || prevQualifiers !== queryQualifiers

  // Update previous values only when fetching completes
  if (!isFetching && isNavigating) {
    setPrevPage(currentPage)
    setPrevTab(activeTab)
    setPrevQualifiers(queryQualifiers)
  }

  // Client-side filtering for old sessions API
  const {openTasks, closedTasks} = tasks.reduce(
    (acc, task) => {
      const isClosed = task.pull && (task.pull.state === 'closed' || task.pull.state === 'merged')
      if (isClosed) {
        acc.closedTasks.push(task)
      } else {
        acc.openTasks.push(task)
      }
      return acc
    },
    {openTasks: [] as typeof tasks, closedTasks: [] as typeof tasks},
  )

  // Use server counts if available, otherwise client-side counts
  const openCount = counts?.open ?? openTasks.length
  const closedCount = counts?.closed ?? closedTasks.length

  // Show skeletons during initial load (no tasks yet) or when navigating (page/tab change)
  // Don't show skeletons for background refetches when we already have data (optimistic updates)
  const showSkeletonLoaders = (isFetching && isNavigating) || isLoading

  // Tasks are already filtered server-side, slice to result count to ensure optimistic tasks don't cause overflow
  const filteredTasks = tasks.slice(0, resultCount)

  const setAgentResultCount = (count: number) => {
    setResultCount(count)
    persistResultCount?.(count)
  }

  const setAgentQueryQualifiers = (qualifiers: AgentQueryQualifier[]) => {
    setQueryQualifiers(qualifiers)
    persistQueryQualifiers?.(qualifiers)
  }

  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const onViewAllAgents = useCallback(() => {
    sendClickAnalyticsEvent({category: analyticsCategory, action: 'click.view_all_agent_tasks'})
  }, [sendClickAnalyticsEvent, analyticsCategory])
  const onTitleClick = useCallback(() => {
    sendClickAnalyticsEvent({category: analyticsCategory, action: 'click.navigate_to_agent_task'})
  }, [sendClickAnalyticsEvent, analyticsCategory])

  const filterMenu = (
    <ListActionMenu
      queryQualifiers={queryQualifiers}
      setQueryQualifiers={setAgentQueryQualifiers}
      setResultCount={setAgentResultCount}
      resultCount={resultCount}
      label="Agent sessions options"
      filterGroupHeading="Agent sessions to include"
      menuItems={[
        {label: 'Active', value: AgentQueryQualifier.Open},
        {label: 'Completed', value: AgentQueryQualifier.Closed},
      ]}
      analyticsEventType={'agent_options.save'}
      analyticsTarget={'AGENTS_LIST_ACTION_MENU'}
      analyticsCategory={analyticsCategory}
    />
  )

  const pillMenu = (
    <div className={styles.pillMenu} role="radiogroup" aria-label="Agent sessions status filter">
      <ListViewSectionFilterLink
        title="Active"
        isSelected={activeTab === 'active'}
        count={openCount}
        isLoading={showSkeletonLoaders}
        href="#"
        onClick={e => {
          e.preventDefault()
          setActiveTab?.('active')
          searchParams.delete('page')
          searchParams.delete('completed')
          setSearchParams(searchParams, {preventAutofocus: true})
        }}
      />
      <ListViewSectionFilterLink
        title="Completed"
        isSelected={activeTab === 'completed'}
        count={closedCount}
        isLoading={showSkeletonLoaders}
        href="#"
        onClick={e => {
          e.preventDefault()
          setActiveTab?.('completed')
          searchParams.delete('page')
          searchParams.set('completed', 'true')
          setSearchParams(searchParams, {preventAutofocus: true})
        }}
      />
    </div>
  )

  return (
    <DashboardListView>
      <DashboardListViewHeader>
        <DashboardListViewHeaderTitle headingLevel="h2">
          {showPillMenu && additionalFilters ? (
            pillMenu
          ) : (
            <Stack direction="horizontal" gap="condensed" align="center">
              {title}
            </Stack>
          )}
        </DashboardListViewHeaderTitle>
        <DashboardListViewHeaderMetadata>
          {showViewAll && tasks.length > 0 ? (
            <Link href={getViewAllSessionsHref()} onClick={onViewAllAgents}>
              View all<span className="sr-only"> sessions</span>
            </Link>
          ) : null}
          {showFilterMenu ? filterMenu : null}
          {showPillMenu && additionalFilters ? additionalFilters : null}
          {showPillMenu && !additionalFilters ? pillMenu : null}
        </DashboardListViewHeaderMetadata>
      </DashboardListViewHeader>
      <DashboardListViewItems>
        {isError ? (
          <DashboardListItem className={styles.emptyItem}>
            <AlertIcon className={styles.noItemsIcon} />
            <DashboardListItemTitle as="p" className={styles.emptyTitle}>
              Unable to load agent tasks, try again later.
            </DashboardListItemTitle>
          </DashboardListItem>
        ) : showSkeletonLoaders ? (
          loadingState
        ) : filteredTasks.length === 0 ? (
          <DashboardListItem className={clsx(styles.emptyItem, enableEmptyListAction && styles.emptyItemWithActions)}>
            {enableEmptyListAction ? (
              <>
                <AgentIcon className={styles.noAgentItemsIcon} />

                <DashboardListItemTitle as="p" className={styles.emptyAgentTitle}>
                  {emptyPlaceholder}
                </DashboardListItemTitle>

                <DashboardListItemActions>
                  <Button as="a" href={getAgentsPath()} variant="default" size="medium">
                    Start a session
                  </Button>
                </DashboardListItemActions>
              </>
            ) : (
              <DashboardListItemTitle as="p" className={styles.emptyTitle}>
                {emptyPlaceholder}
              </DashboardListItemTitle>
            )}
          </DashboardListItem>
        ) : (
          filteredTasks.map(task => (
            <AgentsListItem
              key={task.clientGeneratedId ?? task.id ?? task.resource_id}
              task={task}
              onTitleClick={onTitleClick}
              analyticsCategory={analyticsCategory}
            />
          ))
        )}
      </DashboardListViewItems>
      {showPagination && <AgentsListPagination hasNextPage={hasNextPage} />}
    </DashboardListView>
  )
}
